import axios from "axios";
import { toast } from "react-toastify";
import { BaseType, BaseTypeLiterals } from "../domain/base/base-type";
import { reportAsyncError } from "../../utils/error-reporter";

type StatusCodeLiterals =
  | "BAD_REQUEST"
  | "UNAUTHORIZED"
  | "FORBIDDEN"
  | "NOT_FOUND"
  | "CONFLICT"
  | "INTERNAL_SERVER_ERROR"
  | "UNKNOWN";

export class StatusCode extends BaseType<StatusCodeLiterals> {
  static fromStatusNumber(status: number): StatusCode {
    if (status === 400) {
      return new StatusCode("BAD_REQUEST");
    } else if (status === 401) {
      return new StatusCode("UNAUTHORIZED");
    } else if (status === 403) {
      return new StatusCode("FORBIDDEN");
    } else if (status === 404) {
      return new StatusCode("NOT_FOUND");
    } else if (status === 409) {
      return new StatusCode("CONFLICT");
    } else if (status === 500) {
      return new StatusCode("INTERNAL_SERVER_ERROR");
    } else {
      return new StatusCode("UNKNOWN");
    }
  }
}

export type SmartApplyErrorCodeLiterals =
  | "bad_request"
  | "bad_request.version_outdated"
  | "bad_request.too_large_record_count"
  | "bad_request.invalid_user_email_token"
  | "bad_request.inconsistent_pdf_schema_setting"
  | "bad_request.procedure_not_match"
  | "bad_request.invalid_csv_format"
  | "bad_request.same_as_current_password"
  | "bad_request.invalid_file_mime_type"
  | "unauthorized"
  | "forbidden"
  | "not_found"
  | "conflict"
  | "org_user_already_exists"
  | "custom_url_already_exists"
  | "internal_server_error"
  | "unauthorized.email_auth_required"
  | "unauthorized.tmp_password_expired"
  | "forbidden.not_permitted_network_access"
  | "forbidden.user_login_locked"
  | "forbidden.user_just_login_locked"
  | "applications.cert_verification_pending"
  | "bad_request.invalid_column_length"
  | "bad_request.invalid_application_id_label"
  | "bad_request.invalid_column_header_label"
  | "bad_request.invalid_application_id_format"
  | "bad_request.invalid_character_code"
  | "bad_request.too_large_rows_count"
  | "bad_request.not_found_formula_used_in_payment"
  | "bad_request.not_complete_all_applications"
  | "bad_request.nan_payment_amount"
  | "bad_request.negative_payment_amount"
  | "bad_request.payment_amount_is_less_than_minimum"
  | "bad_request.incalculable_formula"
  | "bad_request.ttzk_portal.shortcut_max_count_error"
  | "bad_request.proc_management.tag_priority_duplication_error"
  | "not_found.procedure_before_publish"
  | "applications.tenant_process_result_unchangeable"
  | "applications.pdf_field_render_failure"
  | "applications.pdf_has_optional_content"
  | "bad_request.invalid_mail_template"
  | "bad_request.multiple_procedure_ids_found"
  | "bad_request.too_many_applications"
  | "default";

export type ErrorCodeMatcher<R> = BaseTypeLiterals<
  SmartApplyErrorCodeLiterals,
  R
>;

export type ErrorResponse = {
  code: SmartApplyErrorCodeLiterals;
  message: string;
};

export class SmartApplyApiError extends Error {
  public name = "ApiError";
  public message: string;
  public response?: ErrorResponse;
  public status?: StatusCode;
  public rawResponse?: Object;
  private blobResponse?: Blob;

  constructor(public error: Error) {
    super();
    Object.setPrototypeOf(this, SmartApplyApiError.prototype);
    this.message = error.message;
    if (axios.isAxiosError(error)) {
      if (error.response) {
        if (error.request && error.request.responseType === "blob") {
          // when responseType of request was blob, json response is stored in error.response.data as blob,
          // so this must be parsed to json asynchronously.
          this.blobResponse = error.response.data as Blob;
        } else {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          this.response = error.response.data as ErrorResponse;
        }
        this.status = StatusCode.fromStatusNumber(error.response.status);
      } else if (error.request) {
        // The request was made but no response was received
        // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
        // http.ClientRequest in node.js
        console.log(error.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        console.log("Error", error.message);
      }
    }
  }

  errorCode() {
    return this.response && this.response ? this.response.code : undefined;
  }

  matchErrorCode<R extends Function>(matcher: ErrorCodeMatcher<R>) {
    const literal =
      this.response && this.response.code ? this.response.code : "default";
    return matcher[literal]();
  }

  isRetryable() {
    return axios.isAxiosError(this.error) && this.error.code === "ECONNABORTED";
  }

  // 内部で保持しているレスポンスデータから、情報の構造化を試みる。
  // errorCode() メソッドなどの呼び出し前に実行されることが期待される。
  //
  // レスポンスデータのパース中にエラーが発生しても、このメソッドが返却する Promise は reject されない。
  // パースエラーが発生した場合は自動でキャッチしてレポートするが、その挙動を抑制したい場合は、
  // 引数 opt.reportParseError を false にすること。
  async resolveErrorResponse(
    opt: { reportParseError: boolean } = { reportParseError: true },
  ): Promise<void> {
    // パース対象が存在しない場合は何もしない
    if (!this.blobResponse) {
      return;
    }

    // 既に構造化されたエラー情報が存在する場合は何もしない
    if (this.response) {
      return;
    }

    return parseBlobRes(this.blobResponse)
      .then((parsed) => {
        // TODO ErrorResponseをエラー種別に応じて拡張可能にするための仕組みを作りたい
        this.rawResponse = parsed;
        this.response = parsed as ErrorResponse;
      })
      .catch((e) => {
        if (opt.reportParseError) {
          reportAsyncError(
            e instanceof Error
              ? e
              : new Error(`Failed to parse blobResponse ${e}`),
          );
        }
      });
  }

  toString() {
    return this.error.toString();
  }

  static is403(e: Error): boolean {
    return SmartApplyApiError.isApiError(e)
      ? e.status?.getValue() === "FORBIDDEN"
      : SmartApplyApiError.isApiError(new SmartApplyApiError(e));
  }

  static isUserLoginLocked(e: Error) {
    return this.isErrorCode(e, "forbidden.user_login_locked");
  }

  static isUserJustLoginLocked(e: Error) {
    return this.isErrorCode(e, "forbidden.user_just_login_locked");
  }

  static is404(e: Error): boolean {
    return SmartApplyApiError.isApiError(e)
      ? e.status?.getValue() === "NOT_FOUND"
      : SmartApplyApiError.is404(new SmartApplyApiError(e));
  }

  static is409(e: Error): boolean {
    return SmartApplyApiError.isApiError(e)
      ? e.status?.getValue() === "CONFLICT"
      : SmartApplyApiError.is409(new SmartApplyApiError(e));
  }

  static isBadRequest(e: Error): boolean {
    return SmartApplyApiError.isApiError(e)
      ? e.status?.getValue() === "BAD_REQUEST"
      : SmartApplyApiError.isBadRequest(new SmartApplyApiError(e));
  }

  static notFoundFormulaUsedInPayment(e: Error) {
    return this.isErrorCode(e, "bad_request.not_found_formula_used_in_payment");
  }

  static orgUserExists(e: Error) {
    return this.isErrorCode(e, "org_user_already_exists");
  }

  static customUrlExists(e: Error) {
    return this.isErrorCode(e, "custom_url_already_exists");
  }

  static tmpPasswordExpired(e: Error) {
    return this.isErrorCode(e, "unauthorized.tmp_password_expired");
  }

  static isRetryable(e: Error) {
    if (SmartApplyApiError.isApiError(e)) {
      return e.isRetryable();
    }
    return new SmartApplyApiError(e).isRetryable();
  }

  static isVersionOutdated(e: Error) {
    return this.isErrorCode(e, "bad_request.version_outdated");
  }

  static isApiError(e: Error): e is SmartApplyApiError {
    return e instanceof SmartApplyApiError;
  }

  static errorCode(e: Error): SmartApplyErrorCodeLiterals | undefined {
    if (SmartApplyApiError.isApiError(e)) {
      return e.errorCode();
    }
    return new SmartApplyApiError(e).errorCode();
  }

  static isErrorCode(e: Error, code: SmartApplyErrorCodeLiterals) {
    if (SmartApplyApiError.isApiError(e)) {
      return e.response && e.response.code === code;
    }
    const apiError = new SmartApplyApiError(e);
    return apiError.response && apiError.response.code === code;
  }

  static handle401Error() {
    toast.error(
      "長時間操作が行われなかったためタイムアウトしました。再度ログインしてください。",
    );
  }

  static checkOrRethrow(e: Error): e is SmartApplyApiError {
    if (e instanceof SmartApplyApiError) {
      return true;
    }
    throw e;
  }

  // ttzkPortal
  static isTtzkPortalShortcutMaxCountError(e: Error) {
    return this.isErrorCode(
      e,
      "bad_request.ttzk_portal.shortcut_max_count_error",
    );
  }
}

const parseBlobRes = (resData: any): Promise<ReturnType<typeof JSON.parse>> => {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = (event) => {
      resolve(JSON.parse(`${event.target?.result}`));
    };
    fr.onerror = (e) => reject(e);
    fr.readAsText(resData);
  });
};
